import { idbGet, idbSet } from '../lib/idb';

/**
 * God rolls for the Item Manager, from DIM's community wish list (48klocs/dim-wish-list-sources, voltron.txt: about
 * 275,000 rolls gathered from Aegis, the PvE podcast, Mercules and others). The list writes most of its "PvE" / "PvP"
 * words once, in a //notes: line above a group of rolls, so the groups are read as such here: each group is one weapon,
 * one note and the perks allowed in each column (the rolls of a group are every mix of those columns). That keeps
 * the whole list small enough to keep in the browser, and an item is a god roll when, in every column of a group, it
 * has one of the perks the group allows.
 */

const WISH_URL = 'https://raw.githubusercontent.com/48klocs/dim-wish-list-sources/master/voltron.txt';
const KEY = 'godrolls:1';

export type RollMode = 'pve' | 'pvp' | 'any';
export interface RollGroup { cols: number[][]; mode: RollMode; note: string }
export type RollTable = Record<number, RollGroup[]>;

const modeOf = (note: string): RollMode => {
  const pve = note.search(/\bpve\b/i), pvp = note.search(/\bpvp\b|crucible|trials/i);
  if (pve < 0 && pvp < 0) return 'any';
  if (pvp < 0) return 'pve';
  if (pve < 0) return 'pvp';
  return pve < pvp ? 'pve' : 'pvp';   // both named: the first one is what the roll is for
};

export function parseWishList(text: string): RollTable {
  const t: RollTable = {};
  let block = '';
  let open: { hash: number; key: string; sets: Set<number>[]; note: string } | undefined;
  const close = () => {
    if (!open) return;
    (t[open.hash] ??= []).push({ cols: open.sets.map((s) => [...s]), mode: modeOf(open.note), note: open.note.slice(0, 300) });
    open = undefined;
  };
  const rx = /^dimwishlist:item=(-?\d+)(?:&perks=([\d,]*))?(?:#notes:(.*))?$/;
  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (line.startsWith('//notes:')) { close(); block = line.slice(8).trim(); continue; }
    if (!line || line.startsWith('title:')) { close(); block = ''; continue; }
    if (!line.startsWith('dimwishlist:')) continue;
    const m = rx.exec(line);
    if (!m || m[1].startsWith('-')) continue;   // a minus: a roll to avoid (not used here)
    const hash = Number(m[1]);
    const perks = (m[2] ?? '').split(',').filter(Boolean).map(Number);
    if (!perks.length) continue;
    const note = (m[3] ?? block).replace(/\|tags:([^|]*)/, ' ($1)').trim();
    const key = `${hash}|${note}|${perks.length}`;
    if (open?.key !== key) { close(); open = { hash, key, sets: perks.map(() => new Set<number>()), note }; }
    perks.forEach((p, k) => open!.sets[k].add(p));
  }
  close();
  return t;
}

let job: Promise<RollTable> | undefined;

/** The table, kept for three days in the browser (the list changes a few times a week). */
export function godRolls(): Promise<RollTable> {
  return (job ??= (async () => {
    const kept = await idbGet<{ at: number; t: RollTable }>(KEY).catch(() => undefined);
    if (kept && Date.now() - kept.at < 3 * 86400e3) return kept.t;
    try {
      const t = parseWishList(await (await fetch(WISH_URL)).text());
      void idbSet(KEY, { at: Date.now(), t });
      return t;
    } catch {
      return kept?.t ?? {};
    }
  })());
}

/** The groups this item's perks satisfy (its plugged perks; any of them may be the enhanced version). */
export function matchingRolls(groups: RollGroup[] | undefined, perks: Set<number>): RollGroup[] {
  if (!groups || !perks.size) return [];
  return groups.filter((g) => g.cols.every((c) => c.some((p) => perks.has(p))));
}

export interface GodMark { pve: boolean; pvp: boolean; any: boolean; groups: RollGroup[] }
export function godMark(groups: RollGroup[] | undefined, perks: Set<number>): GodMark | undefined {
  const hit = matchingRolls(groups, perks);
  if (!hit.length) return undefined;
  return { pve: hit.some((g) => g.mode === 'pve'), pvp: hit.some((g) => g.mode === 'pvp'), any: hit.some((g) => g.mode === 'any'), groups: hit };
}

/**
 * The same test by perk name: an enhanced perk is a different item with the same name as the plain one, and the list
 * names one or the other. names: plug hash -> name (Bungie's item table).
 */
const byName = new WeakMap<RollGroup, Set<string>[]>();
export function godMarkByName(groups: RollGroup[] | undefined, perks: Set<string>, names: (hash: number) => string | undefined): GodMark | undefined {
  if (!groups || !perks.size) return undefined;
  const hit = groups.filter((g) => {
    let cols = byName.get(g);
    if (!cols) { cols = g.cols.map((c) => new Set(c.map(names).filter((n): n is string => !!n))); byName.set(g, cols); }
    return cols.every((c) => c.size > 0 && [...c].some((n) => perks.has(n)));
  });
  if (!hit.length) return undefined;
  return { pve: hit.some((g) => g.mode === 'pve'), pvp: hit.some((g) => g.mode === 'pvp'), any: hit.some((g) => g.mode === 'any'), groups: hit };
}
